/**
 * Hook for fetching learning assignments (curriculum and course assignments to users)
 * This is different from module assignments which are homework/quiz assignments within a course.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Assignment, AssignmentStatus } from "@/types/assignments";

function determineStatus(
  assignmentCompletedAt: string | null,
  enrollmentExists: boolean,
  enrollmentCompletedAt: string | null
): AssignmentStatus {
  // If the assignment itself is marked complete
  if (assignmentCompletedAt) {
    return 'completed';
  }
  
  // Check enrollment status
  if (enrollmentExists) {
    if (enrollmentCompletedAt) {
      return 'completed';
    }
    return 'in_progress';
  }
  
  return 'not_started';
}

async function fetchCurriculumAssignments(): Promise<Assignment[]> {
  // Fetch curriculum assignments
  const { data: assignments, error: assignmentsError } = await supabase
    .from("curriculum_assignments")
    .select(`
      id,
      curriculum_id,
      user_id,
      assigned_by,
      assigned_at,
      acknowledged_at,
      due_date,
      required,
      notes,
      completed_at
    `)
    .order("assigned_at", { ascending: false });

  if (assignmentsError) throw assignmentsError;
  if (!assignments || assignments.length === 0) return [];

  // Get unique user IDs and curriculum IDs
  const userIds = [...new Set(assignments.map(a => a.user_id))];
  const assignedByIds = [...new Set(assignments.map(a => a.assigned_by))];
  const curriculumIds = [...new Set(assignments.map(a => a.curriculum_id))];

  // Fetch profiles for users and assigners
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, full_name, avatar_url")
    .in("id", [...userIds, ...assignedByIds]);

  // Fetch curricula
  const { data: curricula } = await supabase
    .from("curricula")
    .select("id, title")
    .in("id", curriculumIds);

  // Fetch enrollments to determine status
  const { data: enrollments } = await supabase
    .from("curriculum_enrollments")
    .select("user_id, curriculum_id, completed_at")
    .in("user_id", userIds)
    .in("curriculum_id", curriculumIds);

  // Create lookup maps
  const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
  const curriculumMap = new Map(curricula?.map(c => [c.id, c]) || []);
  const enrollmentMap = new Map(
    enrollments?.map(e => [`${e.user_id}-${e.curriculum_id}`, e]) || []
  );

  return assignments.map(assignment => {
    const user = profileMap.get(assignment.user_id);
    const assigner = profileMap.get(assignment.assigned_by);
    const curriculum = curriculumMap.get(assignment.curriculum_id);
    const enrollmentKey = `${assignment.user_id}-${assignment.curriculum_id}`;
    const enrollment = enrollmentMap.get(enrollmentKey);

    const status = determineStatus(
      assignment.completed_at,
      !!enrollment,
      enrollment?.completed_at || null
    );

    return {
      id: assignment.id,
      user_id: assignment.user_id,
      user_name: user?.full_name || "Unknown User",
      user_avatar: user?.avatar_url || undefined,
      type: 'curriculum' as const,
      item_id: assignment.curriculum_id,
      item_name: curriculum?.title || "Unknown Curriculum",
      assigned_by_id: assignment.assigned_by,
      assigned_by_name: assigner?.full_name || "Unknown",
      assigned_at: assignment.assigned_at,
      due_date: assignment.due_date || undefined,
      required: assignment.required,
      notes: assignment.notes || undefined,
      status,
      completed_at: assignment.completed_at || undefined,
      acknowledged_at: assignment.acknowledged_at || undefined,
    };
  });
}

async function fetchCourseAssignments(): Promise<Assignment[]> {
  // Fetch course assignments
  const { data: assignments, error: assignmentsError } = await supabase
    .from("course_assignments")
    .select(`
      id,
      course_id,
      user_id,
      assigned_by,
      assigned_at,
      acknowledged_at,
      due_date,
      required,
      notes,
      completed_at
    `)
    .order("assigned_at", { ascending: false });

  if (assignmentsError) throw assignmentsError;
  if (!assignments || assignments.length === 0) return [];

  // Get unique user IDs and course IDs
  const userIds = [...new Set(assignments.map(a => a.user_id))];
  const assignedByIds = [...new Set(assignments.map(a => a.assigned_by))];
  const courseIds = [...new Set(assignments.map(a => a.course_id))];

  // Fetch profiles for users and assigners
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, full_name, avatar_url")
    .in("id", [...userIds, ...assignedByIds]);

  // Fetch courses
  const { data: courses } = await supabase
    .from("courses")
    .select("id, title")
    .in("id", courseIds);

  // Fetch enrollments to determine status
  const { data: enrollments } = await supabase
    .from("course_enrollments")
    .select("user_id, course_id, completed_at")
    .in("user_id", userIds)
    .in("course_id", courseIds);

  // Create lookup maps
  const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
  const courseMap = new Map(courses?.map(c => [c.id, c]) || []);
  const enrollmentMap = new Map(
    enrollments?.map(e => [`${e.user_id}-${e.course_id}`, e]) || []
  );

  return assignments.map(assignment => {
    const user = profileMap.get(assignment.user_id);
    const assigner = profileMap.get(assignment.assigned_by);
    const course = courseMap.get(assignment.course_id);
    const enrollmentKey = `${assignment.user_id}-${assignment.course_id}`;
    const enrollment = enrollmentMap.get(enrollmentKey);

    const status = determineStatus(
      assignment.completed_at,
      !!enrollment,
      enrollment?.completed_at || null
    );

    return {
      id: assignment.id,
      user_id: assignment.user_id,
      user_name: user?.full_name || "Unknown User",
      user_avatar: user?.avatar_url || undefined,
      type: 'course' as const,
      item_id: assignment.course_id,
      item_name: course?.title || "Unknown Course",
      assigned_by_id: assignment.assigned_by,
      assigned_by_name: assigner?.full_name || "Unknown",
      assigned_at: assignment.assigned_at,
      due_date: assignment.due_date || undefined,
      required: assignment.required,
      notes: assignment.notes || undefined,
      status,
      completed_at: assignment.completed_at || undefined,
      acknowledged_at: assignment.acknowledged_at || undefined,
    };
  });
}

/**
 * Fetches all learning assignments (both curricula and courses)
 * Used by admins to manage assignments
 */
export function useAllLearningAssignments() {
  return useQuery({
    queryKey: ["all_learning_assignments"],
    queryFn: async () => {
      const [curriculumAssignments, courseAssignments] = await Promise.all([
        fetchCurriculumAssignments(),
        fetchCourseAssignments(),
      ]);

      // Combine and sort by assigned_at descending
      const allAssignments = [...curriculumAssignments, ...courseAssignments];
      allAssignments.sort((a, b) => 
        new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime()
      );

      return allAssignments;
    },
  });
}

/**
 * Fetches learning assignments for a specific user
 */
export function useUserLearningAssignments(userId: string) {
  return useQuery({
    queryKey: ["user_learning_assignments", userId],
    queryFn: async () => {
      const [curriculumAssignments, courseAssignments] = await Promise.all([
        fetchCurriculumAssignmentsForUser(userId),
        fetchCourseAssignmentsForUser(userId),
      ]);

      const allAssignments = [...curriculumAssignments, ...courseAssignments];
      allAssignments.sort((a, b) => 
        new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime()
      );

      return allAssignments;
    },
    enabled: !!userId,
  });
}

async function fetchCurriculumAssignmentsForUser(userId: string): Promise<Assignment[]> {
  const { data: assignments, error } = await supabase
    .from("curriculum_assignments")
    .select(`
      id,
      curriculum_id,
      user_id,
      assigned_by,
      assigned_at,
      acknowledged_at,
      due_date,
      required,
      notes,
      completed_at
    `)
    .eq("user_id", userId)
    .order("assigned_at", { ascending: false });

  if (error) throw error;
  if (!assignments || assignments.length === 0) return [];

  const curriculumIds = [...new Set(assignments.map(a => a.curriculum_id))];
  const assignedByIds = [...new Set(assignments.map(a => a.assigned_by))];

  const [{ data: profiles }, { data: curricula }, { data: enrollments }] = await Promise.all([
    supabase.from("profiles").select("id, full_name, avatar_url").in("id", [userId, ...assignedByIds]),
    supabase.from("curricula").select("id, title").in("id", curriculumIds),
    supabase.from("curriculum_enrollments").select("curriculum_id, completed_at").eq("user_id", userId).in("curriculum_id", curriculumIds),
  ]);

  const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
  const curriculumMap = new Map(curricula?.map(c => [c.id, c]) || []);
  const enrollmentMap = new Map(enrollments?.map(e => [e.curriculum_id, e]) || []);

  return assignments.map(assignment => {
    const user = profileMap.get(userId);
    const assigner = profileMap.get(assignment.assigned_by);
    const curriculum = curriculumMap.get(assignment.curriculum_id);
    const enrollment = enrollmentMap.get(assignment.curriculum_id);

    const status = determineStatus(
      assignment.completed_at,
      !!enrollment,
      enrollment?.completed_at || null
    );

    return {
      id: assignment.id,
      user_id: assignment.user_id,
      user_name: user?.full_name || "Unknown User",
      user_avatar: user?.avatar_url || undefined,
      type: 'curriculum' as const,
      item_id: assignment.curriculum_id,
      item_name: curriculum?.title || "Unknown Curriculum",
      assigned_by_id: assignment.assigned_by,
      assigned_by_name: assigner?.full_name || "Unknown",
      assigned_at: assignment.assigned_at,
      due_date: assignment.due_date || undefined,
      required: assignment.required,
      notes: assignment.notes || undefined,
      status,
      completed_at: assignment.completed_at || undefined,
      acknowledged_at: assignment.acknowledged_at || undefined,
    };
  });
}

async function fetchCourseAssignmentsForUser(userId: string): Promise<Assignment[]> {
  const { data: assignments, error } = await supabase
    .from("course_assignments")
    .select(`
      id,
      course_id,
      user_id,
      assigned_by,
      assigned_at,
      acknowledged_at,
      due_date,
      required,
      notes,
      completed_at
    `)
    .eq("user_id", userId)
    .order("assigned_at", { ascending: false });

  if (error) throw error;
  if (!assignments || assignments.length === 0) return [];

  const courseIds = [...new Set(assignments.map(a => a.course_id))];
  const assignedByIds = [...new Set(assignments.map(a => a.assigned_by))];

  const [{ data: profiles }, { data: courses }, { data: enrollments }] = await Promise.all([
    supabase.from("profiles").select("id, full_name, avatar_url").in("id", [userId, ...assignedByIds]),
    supabase.from("courses").select("id, title").in("id", courseIds),
    supabase.from("course_enrollments").select("course_id, completed_at").eq("user_id", userId).in("course_id", courseIds),
  ]);

  const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
  const courseMap = new Map(courses?.map(c => [c.id, c]) || []);
  const enrollmentMap = new Map(enrollments?.map(e => [e.course_id, e]) || []);

  return assignments.map(assignment => {
    const user = profileMap.get(userId);
    const assigner = profileMap.get(assignment.assigned_by);
    const course = courseMap.get(assignment.course_id);
    const enrollment = enrollmentMap.get(assignment.course_id);

    const status = determineStatus(
      assignment.completed_at,
      !!enrollment,
      enrollment?.completed_at || null
    );

    return {
      id: assignment.id,
      user_id: assignment.user_id,
      user_name: user?.full_name || "Unknown User",
      user_avatar: user?.avatar_url || undefined,
      type: 'course' as const,
      item_id: assignment.course_id,
      item_name: course?.title || "Unknown Course",
      assigned_by_id: assignment.assigned_by,
      assigned_by_name: assigner?.full_name || "Unknown",
      assigned_at: assignment.assigned_at,
      due_date: assignment.due_date || undefined,
      required: assignment.required,
      notes: assignment.notes || undefined,
      status,
      completed_at: assignment.completed_at || undefined,
      acknowledged_at: assignment.acknowledged_at || undefined,
    };
  });
}
